import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getServiceConfig, getServiceUrl } from '../config/services'
import { ServiceType } from '../types'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { productsPageFooter } from './layout/footerConfigs'

interface Product {
  id: string
  name: string
  description: string
  category: 'software' | 'hardware' | 'services'
  link: string
  isExternal?: boolean
  isBlurred?: boolean
  comingSoon?: boolean
}

// Get Rare Earth service URL (localhost in dev, rare.mernaki.de in production)
const getRareEarthUrl = () => {
  const config = getServiceConfig(ServiceType.RARE_EARTH_PERMIT)
  return config ? getServiceUrl(config) : '/products/rare-earth-permit'
}

const products: Product[] = [
  // SOFTWARE
  {
    id: 'jobmeet',
    name: 'JobMeet',
    description: 'The premium appointment scheduling tool for modern recruiters.',
    category: 'software',
    link: '/products/jobmeet',
    isBlurred: false,
    comingSoon: false
  },
  {
    id: 'codered-ide',
    name: 'Project Crimson',
    description: 'AI-native development environment for modern creators.',
    category: 'software',
    link: '/products/codered-ide',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'beyondreal',
    name: 'Project Nexus',
    description: 'Tools that supercharge developers building in Unreal Engine 5.',
    category: 'software',
    link: '/products/beyondreal',
    isBlurred: true,
    comingSoon: true
  },
  // HARDWARE
  {
    id: 'macx-launcher',
    name: 'Project Velocity',
    description: 'macOS game launcher built on Apple Game Porting Toolkit.',
    category: 'hardware',
    link: '/products/macx-launcher',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'macx-handheld',
    name: 'Project Handheld',
    description: 'Ultra-Retina gaming handheld powered by redesigned Mac Mini architecture.',
    category: 'hardware',
    link: '/products/macx-handheld',
    isBlurred: true,
    comingSoon: true
  },
  // SERVICES
  {
    id: 'rare-earth-permit',
    name: 'Rare Earth Permit',
    description: 'Automated permit approval system for mineral buyers and sellers.',
    category: 'services',
    link: getRareEarthUrl(),
    isExternal: true
  },
  {
    id: 'hr-templates',
    name: 'HR Templates',
    description: 'Beautifully designed recruiting pages for modern teams.',
    category: 'services',
    link: '/products/hr-templates',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'verpackg',
    name: 'VerpackG Löschung',
    description: 'Fast, automated EPR (VerpackG) deregistration service.',
    category: 'services',
    link: '/products/verpackg',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: '5g-recommerce',
    name: '5G Recommerce',
    description: 'AI-backed mobile trade-in intelligence platform.',
    category: 'services',
    link: '/products/5g-recommerce',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'raid-to-revenue',
    name: 'Raid to Revenue',
    description: 'Revenue intelligence for sourcing profitable trade opportunities.',
    category: 'services',
    link: '/products/raid-to-revenue',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'compliance-recall',
    name: 'Compliance Recall',
    description: 'Automated compliance document recall + verification.',
    category: 'services',
    link: '/products/compliance-recall',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'job-scraper',
    name: 'EuropeanJobScraper',
    description: 'High-precision hiring-intent scraper for European companies.',
    category: 'services',
    link: '/products/job-scraper',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'integrations',
    name: 'Mernaki Integrations',
    description: 'Installation, deployment, and automation setup for companies.',
    category: 'services',
    link: '/products/integrations',
    isBlurred: true,
    comingSoon: true
  },
  {
    id: 'consulting',
    name: 'Business Automation Consulting',
    description: 'Custom systems built for workflow, compliance, or operations.',
    category: 'services',
    link: '/products/consulting',
    isBlurred: true,
    comingSoon: true
  }
]

const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const cardInner = (
        <div
          className="relative p-6 rounded-2xl transition-all duration-500"
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
            height: '100%',
            filter: product.isBlurred ? 'blur(4px)' : 'none',
            pointerEvents: product.isBlurred ? 'none' : 'auto'
          }}
        >
          {product.comingSoon && (
            <div
              className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs"
              style={{
                background: 'rgba(74, 144, 226, 0.15)',
                color: '#4A90E2',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                border: '1px solid rgba(74, 144, 226, 0.2)'
              }}
            >
              Coming Soon
            </div>
          )}

          <h3
            className="text-xl mb-3"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            {product.name}
          </h3>

          <p
            className="mb-4"
            style={{
              color: '#666666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontSize: '15px',
              lineHeight: '1.6'
            }}
          >
            {product.description}
          </p>

          <div
            className="inline-flex items-center gap-2 text-sm transition-all duration-300"
            style={{
              color: '#4A90E2',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600
            }}
          >
            Explore {product.name}
            <span
              className="transition-transform duration-300"
              style={{
                display: 'inline-block',
                transform: 'translateX(0)'
              }}
            >
              →
            </span>
          </div>
        </div>
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {product.isExternal ? (
        <a
          href={product.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block group"
          style={{ textDecoration: 'none' }}
        >
          {cardInner}
        </a>
      ) : (
        <Link
          to={product.link}
          className="block group"
          style={{ textDecoration: 'none' }}
        >
          {cardInner}
        </Link>
      )}
    </motion.div>
  )
}

export const ProductsPage: React.FC = () => {
  const isMobile = useIsMobile()
  const [activeCategory, setActiveCategory] = React.useState<'all' | 'software' | 'hardware' | 'services'>('all')

  const prefersReducedMotion = React.useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory)

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}





      {/* Main Content Wrapper with Rounded Bottom Corners */}
      <div 
        className="min-h-screen relative"
        style={{ 
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0 
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})` 
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                  scale: [1, 1.2, 1],
                  x: [0, 50, 0],
                  y: [0, 30, 0],
                }
          }
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                  scale: [1, 1.3, 1],
                  x: [0, -50, 0],
                  y: [0, -30, 0],
                }
          }
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                  scale: [1, 1.15, 1],
                  x: [0, 30, 0],
                  y: [0, -20, 0],
                }
          }
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Hero Section */}
        <section className="relative flex items-center justify-center px-6" style={{ minHeight: '100vh' }}>
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
            className="text-5xl md:text-6xl mb-6"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: '1.1'
            }}
          >
            Products that accelerate businesses and creators
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
            className="text-xl max-w-3xl mx-auto"
            style={{
              color: '#666666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              lineHeight: '1.6'
            }}
          >
            From compliance tools to breakthrough hardware and developer software—Mernaki builds the systems that power tomorrow's companies.
          </motion.p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="relative py-12 px-6" style={{ marginTop: '-20vh' }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
            className="flex justify-center gap-4 flex-wrap"
          >
            {(['all', 'software', 'hardware', 'services'] as const).map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className="px-6 py-3 rounded-xl transition-all duration-300"
                style={{
                  background: activeCategory === category 
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: activeCategory === category 
                    ? '1px solid rgba(0, 0, 0, 0.15)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '14px',
                  fontWeight: activeCategory === category ? 600 : 500,
                  cursor: 'pointer',
                  boxShadow: activeCategory === category 
                    ? '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
                    : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  textTransform: 'capitalize'
                }}
              >
                {category === 'all' ? 'All Products' : category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Product Grid */}
      <section id="product-grid" className="relative py-12 px-6 pb-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcase */}
      <section className="relative py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
            className="text-4xl text-center mb-16"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}
          >
            What connects all our products
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Built with AI', description: 'Intelligent automation at the core' },
              { title: 'Enterprise-grade security', description: 'Bank-level encryption and compliance' },
              { title: 'German-based infrastructure', description: 'GDPR-compliant hosting' },
              { title: 'Lightning-fast support', description: '24/7 expert assistance' },
              { title: 'Unified ecosystem', description: 'Seamless integration across products' },
              { title: 'Modern minimal interface', description: 'Beautiful, intuitive design' }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
                }}
              >
                <h3
                  className="text-lg mb-2"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}
                >
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

        {/* Call to Action */}
        <section className="relative py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
            className="text-4xl md:text-5xl mb-6"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}
          >
            Ready to start?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
            className="text-xl mb-10"
            style={{
              color: '#666666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              lineHeight: '1.6'
            }}
          >
            Book a consultation or try any product instantly.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
            className="flex gap-4 justify-center flex-wrap"
          >
            <Link
              to="/contact"
              className="px-8 py-4 rounded-xl transition-all duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)'
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              }}
            >
              Contact Sales
            </Link>

            <Link
              to="/docs"
              className="px-8 py-4 rounded-xl transition-all duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)'
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              }}
            >
              View Documentation
            </Link>
          </motion.div>
        </div>
      </section>
      </div>

      {/* Footer - Conditional Mobile/Desktop */}
      {isMobile ? (
        <PageMobileFooter {...productsPageFooter} />
      ) : (
        <footer
          className="relative"
          style={{ 
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '140px', // Extended padding to create background behind rounded corners
            paddingBottom: '40px',
            marginTop: '-80px' // Pull footer up to extend background behind page content
          }}
      >
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Header */}
          <div className="text-center mb-12 pb-8 pt-16 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <h3 
              className="text-2xl mb-2"
              style={{ 
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
            >
              Products
            </h3>
            <p 
              className="text-sm"
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Explore our ecosystem
            </p>
          </div>

          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <div>
                  <h3 
                    className="text-lg"
                    style={{ 
                      color: '#FFFFFF',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontWeight: 400,
                      marginBottom: '4px'
                    }}
                  >
                    Mernaki
                  </h3>
                  <p 
                    className="text-xs"
                    style={{ 
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    Service • Software • Hardware
                  </p>
                </div>
              </div>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Company
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { name: 'About', link: '/about' },
                  { name: 'Partnerships', link: '/partnerships' },
                  { name: 'Contact', link: '/contact' }
                ].map((item) => (
                  <li key={item.name} style={{ marginBottom: '12px' }}>
                    <Link
                      to={item.link}
                      style={{
                        color: '#999999',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '14px',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Support */}
            <div>
              <h3
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Support
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { name: 'Help Center', link: '/help' },
                  { name: 'Documentation', link: '/docs' },
                  { name: 'API Reference', link: '/developer/api' },
                  { name: 'Status Page', link: '/status' }
                ].map((item) => (
                  <li key={item.name} style={{ marginBottom: '12px' }}>
                    <Link
                      to={item.link}
                      style={{
                        color: '#999999',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '14px',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 - Legal */}
            <div>
              <h3
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Legal
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { name: 'Privacy Policy', link: '/privacy-policy' },
                  { name: 'Terms of Service', link: '/terms' },
                  { name: 'Cookie Policy', link: '/cookies' },
                  { name: 'Impressum', link: '/impressum' }
                ].map((item) => (
                  <li key={item.name} style={{ marginBottom: '12px' }}>
                    <Link
                      to={item.link}
                      style={{
                        color: '#999999',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '14px',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div
            className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4"
            style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <p
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontSize: '14px',
                margin: 0
              }}
            >
              © {new Date().getFullYear()} Mernaki. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      )}
    </>
  )
}

export default ProductsPage
